import styled from "@emotion/styled";
import { FC, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router";

import { CodeBlack, ContentCopy } from "@livechat/design-system-icons";
import { Alert, Button, Icon, SpacingToken, Text } from "@livechat/design-system-react-components";

import { AccordionCard } from "components/accordion-card/AccordionCard";
import { CodeSnippet } from "components/code-snippet/CodeSnippet";
import { CopyToClipboard } from "components/copy-to-clipboard-input";
import Block from "components/layout/Block";
import Flex from "components/layout/Flex";
import { ToastContent, getToastContent } from "constants/toasts";
import { EventPlace, trackEngagement } from "helpers/analytics";
import { selectLicenseId } from "store/entities/license";
import { ToastVariant, ToastsActions } from "store/features/toasts";
import { withVar } from "themes/helpers";

import { generateEmbedCode } from "./helpers";

enum PublishAccordion {
  Website = "website",
  Livechat = "livechat",
}

export const Publish: FC = () => {
  const licenseID = useSelector(selectLicenseId);
  const formId = useParams<{ id: string }>().id;

  const canPublish = !!formId;

  const embedCode = generateEmbedCode({ licenseID, id: formId });
  const dispatch = useDispatch();

  const [openAccordion, setOpenAccordion] = useState<PublishAccordion | null>(
    canPublish ? PublishAccordion.Website : null
  );

  const handleToggleAccordion = (accordion: PublishAccordion): void => {
    setOpenAccordion(prev => (prev === accordion ? null : accordion));
  };

  const showCopyToast = (): void => {
    trackEngagement("Settings: Contact form embed code copied", EventPlace.Settings);
    dispatch(
      ToastsActions.createToast({
        variant: ToastVariant.Success,
        content: getToastContent(ToastContent.CONTACT_FORM_CODE_COPIED),
      })
    );
  };

  return (
    <Flex
      column
      padding={`${withVar(SpacingToken.Spacing12)} ${withVar(SpacingToken.Spacing16)}`}
      gap={withVar(SpacingToken.Spacing4)}
      alignItems="center"
      maxWidth="482px"
      width="100%"
      margin="auto"
    >
      {!canPublish && (
        <Alert kind="info">
          To use the contact form, click the &quot;Create contact form&quot; button and complete the setup process.
        </Alert>
      )}
      <FullWidthAccordionCard
        dataTestId="publish-website"
        disabled={!canPublish}
        title="Publish on website"
        icon={<Icon source={CodeBlack} />}
        isOpen={openAccordion === PublishAccordion.Website}
        onToggle={() => handleToggleAccordion(PublishAccordion.Website)}
      >
        <Flex column gap={withVar(SpacingToken.Spacing4)}>
          <Text>
            Copy the code below and paste it into your website to add the contact form. Refresh your website to see it
            live.
          </Text>
          <Block>
            <CopyToClipboard value={embedCode} onCopiedHandler={showCopyToast}>
              {({ handleCopy }) => (
                <Button size="compact" kind="primary" icon={<Icon source={ContentCopy} />} onClick={handleCopy}>
                  Copy code
                </Button>
              )}
            </CopyToClipboard>
          </Block>
          <CodeSnippet>{embedCode}</CodeSnippet>
        </Flex>
      </FullWidthAccordionCard>
    </Flex>
  );
};

const FullWidthAccordionCard = styled(AccordionCard)`
  width: 100%;
`;
